import React, { useState } from 'react';

/**
 * "Request access" while TinyCMS is invite-only: an email and how the person
 * would use it, sent to POST /api/access-requests (staff see them in the ops
 * portal). Opens in place under the invite-code field.
 */

const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';
const MAX = 1000;

export function RequestAccess({
  initialEmail = '',
  onClose,
}: {
  initialEmail?: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [useCase, setUseCase] = useState('');
  const [trap, setTrap] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError('Enter a valid email address');
    if (useCase.trim().length < 3) return setError('Tell us a little about how you’d use TinyCMS');
    setSending(true);
    try {
      const res = await fetch(`${backendHost}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), useCase: useCase.trim(), website: trap }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || 'Couldn’t send that. Please try again.');
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t send that. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flow-request" role="status">
        <h1 className="flow-h1">Thanks, we have your request.</h1>
        <p className="flow-lede">We’ll email {email.trim()} when there’s a spot for you.</p>
        <button
          type="button"
          className="flow-link flow-action"
          onClick={onClose}
          style={{ alignSelf: 'start' }}
        >
          Back to sign up
        </button>
      </div>
    );
  }

  return (
    // A separate form (not nested in the signup form): its own submit.
    <form className="flow-request" onSubmit={submit} noValidate>
      <h1 className="flow-h1">Request access</h1>
      <p className="flow-lede">
        TinyCMS is invite-only for now. Leave your email and we’ll be in touch.
      </p>
      <label className="flow-field">
        <span className="flow-label">Email</span>
        <input
          className="flow-input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label className="flow-field">
        <span className="flow-label">How would you use TinyCMS?</span>
        <textarea
          className="flow-input"
          style={{ height: 'auto', minHeight: 96, padding: '10px 14px', resize: 'vertical' }}
          maxLength={MAX}
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
        />
        {useCase.length > MAX - 100 ? (
          <span className="flow-hint">{MAX - useCase.length} characters left</span>
        ) : null}
      </label>
      {/* Left empty by people; bots fill it in. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={trap}
        onChange={(e) => setTrap(e.target.value)}
        style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }}
      />
      {error ? (
        <span className="flow-hint" data-tone="error" role="alert">
          {error}
        </span>
      ) : null}
      {/* The shared button's top margin moves to the row, so Cancel lines up with it. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
        <button
          type="submit"
          className="flow-btn"
          disabled={sending}
          style={{ flex: 1, marginTop: 0 }}
        >
          {sending ? 'Sending…' : 'Request access'}
        </button>
        <button type="button" className="flow-link flow-action" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
